import {
  addField,
  createSchema,
  setDisplayField,
  setDisplayName,
  setPermissions,
} from '@wix/wix-code-collections-api'
import type { CollectionsApi, Patch } from '@wix/wix-code-collections-api'
import type { WixDataApi } from '@wix/wix-data-core'
import { FieldType } from '@wix/wix-data-schema-types'
import type { FieldSpec, Schema } from '@wix/wix-data-schema-types'
import { generateCollectionIdAndName } from './generateCollectionIdAndName'
import { get } from 'lodash'
import type { PresetCollectionRecords } from '../business-logic/dynamic-pages'

const addPattern = (fieldConfig: FieldSpec) =>
  fieldConfig.type === FieldType.slug ? { pattern: fieldConfig.pattern } : {}

const addPii = (fieldConfig: FieldSpec) =>
  fieldConfig.pii ? { pii: fieldConfig.pii } : {}

export const createAndPopulatePresetCollection = async ({
  collectionsApi,
  collectionId,
  collectionName,
  wixDataApi,
  schema,
  records,
  pluginsPatches = [],
}: {
  collectionsApi: CollectionsApi
  collectionId: string
  collectionName: string
  schema: Schema
  records: PresetCollectionRecords
  wixDataApi: WixDataApi
  pluginsPatches?: Patch[]
}) => {
  await collectionsApi.execute(
    createSchema(
      collectionId,
      setDisplayName(collectionName),
      ...(schema.displayField ? [setDisplayField(schema.displayField)] : []),
      setPermissions(schema.permissions),
      ...pluginsPatches,
      ...Object.entries(schema.fields)
        .filter(([_fieldKey, fieldConfig]) => !fieldConfig.systemField)
        .map(([fieldKey, fieldConfig]) =>
          addField(fieldKey, {
            displayName: fieldConfig.displayName,
            type: fieldConfig.type,
            description: fieldConfig.description,
            ...addPattern(fieldConfig),
            ...addPii(fieldConfig),
          }),
        ),
    ),
  )

  const recordsToRemove = records
    .filter(record => '_id' in record)
    .map(record => record._id)

  if (recordsToRemove.length) {
    await wixDataApi.bulkRemove(collectionId, recordsToRemove, {})
  }

  const fixDateValues = (record: object) =>
    Object.entries(record).reduce(
      (acc, [fieldKey, value]) => ({
        ...acc,
        [fieldKey]:
          get(schema.fields, [fieldKey, 'type']) === FieldType.dateTime
            ? new Date(value)
            : value,
      }),
      {},
    )

  const result = records.map(fixDateValues)

  await wixDataApi.bulkInsert(collectionId, result, {})
}

export const createAndPopulateGeneratedPresetCollection = async ({
  collectionsApi,
  wixDataApi,
  schema,
  records,
}: {
  collectionsApi: CollectionsApi
  schema: Schema
  records: PresetCollectionRecords
  wixDataApi: WixDataApi
}) => {
  const allCollections = await collectionsApi.getAll()

  const { collectionId, collectionName } = generateCollectionIdAndName({
    allCollections,
    baseName: schema.displayName,
  })

  await createAndPopulatePresetCollection({
    collectionsApi,
    collectionId,
    collectionName,
    wixDataApi,
    schema,
    records,
  })

  return { collectionId, collectionName }
}
